/** JavaScript hints for the structured editor. Domain validation stays with the host. */
export type ConfigurationValue =
  | null
  | string
  | number
  | boolean
  | ConfigurationValue[]
  | { [key: string]: ConfigurationValue };
export type ConfigurationType =
  "object" | "array" | "string" | "number" | "boolean" | "null";
export interface ConfigurationField {
  title?: string;
  description?: string;
  type?: ConfigurationType;
  default?: ConfigurationValue;
  create?: () => unknown;
  choices?: { label: string; value: ConfigurationValue }[];
  properties?: Record<string, ConfigurationField>;
  items?: ConfigurationField;
  additionalProperties?: ConfigurationField | false;
  required?: string[];
  min?: number;
  max?: number;
  step?: number;
}
export function configurationType(
  value: ConfigurationValue,
): ConfigurationType {
  return value === null
    ? "null"
    : Array.isArray(value)
      ? "array"
      : (typeof value as ConfigurationType);
}
export function configurationDefault(
  field?: ConfigurationField,
  type = field?.type || "string",
): ConfigurationValue {
  if (field?.create) return parseConfiguration(JSON.stringify(field.create()));
  if (field?.default !== undefined)
    return JSON.parse(JSON.stringify(field.default));
  if (field?.choices?.length)
    return JSON.parse(JSON.stringify(field.choices[0].value));
  switch (type) {
    case "object":
      return Object.fromEntries(
        Object.entries(field?.properties || {})
          .filter(([key]) => field?.required?.includes(key))
          .map(([key, value]) => [key, configurationDefault(value)]),
      );
    case "array":
      return [];
    case "number":
      return field?.min ?? 0;
    case "boolean":
      return false;
    case "null":
      return null;
    default:
      return "";
  }
}
/** Validate iteratively before rendering a potentially large imported definition. */
export function parseConfiguration(text: string): ConfigurationValue {
  if (text.length > 12_000_000)
    throw new Error("Configuration exceeds the 12 MB editor limit.");
  const value = JSON.parse(text) as ConfigurationValue;
  const pending: [ConfigurationValue, number][] = [[value, 0]];
  let count = 0;
  while (pending.length) {
    const [entry, depth] = pending.pop()!;
    if (++count > 500_000 || depth > 48)
      throw new Error(
        "Configuration exceeds the editor's 48 level or 500,000 value limit.",
      );
    if (entry && typeof entry === "object")
      for (const child of Object.values(entry))
        pending.push([child, depth + 1]);
    else if (typeof entry === "number" && !Number.isFinite(entry))
      throw new Error("Numbers must be finite.");
  }
  return value;
}
export function renameConfigurationProperty(
  value: Record<string, ConfigurationValue>,
  previous: string,
  next: string,
) {
  if (next !== previous && Object.hasOwn(value, next))
    throw new Error(`A property named ${next} already exists.`);
  return Object.fromEntries(
    Object.entries(value).map(([key, entry]) => [
      key === previous ? next : key,
      entry,
    ]),
  );
}
